// /utils/redux/candidateSlice.js

import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from "@reduxjs/toolkit";

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

const candidatesAdapter = createEntityAdapter({
  selectId: (candidate) => candidate.uuid,
  sortComparer: (a, b) => (b.Score ?? 0) - (a.Score ?? 0),
});

// Async Thunk for fetching candidates
export const fetchCandidates = createAsyncThunk(
  "candidates/fetchCandidates",
  async ({ tenantID, jobID }) => {
    const response = await fetch(
      `${API_ENDPOINT}api/v1/tenants/${tenantID}/jobpostings/${jobID}/applications`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      }
    );
    const data = await response.json();
    if (data.user) {
      return { allCandidates: data.user, candidates: data.user, selectedCandidates: data.user, tenantId: tenantID, jobUuid: jobID };
    } else {
      throw new Error("Candidates not found or invalid response.");
    }
  }
);

// Async Thunk for updating candidate status
export const updateCandidateStatus = createAsyncThunk(
  "candidates/updateCandidateStatus",
  async ({ candidate, newStatus, tenantId, jobUuid }) => {
    const data = {
      ContactInformation: candidate.ContactInformation,
      FullName: candidate.FullName,
    };
    const response = await fetch(
      `${API_ENDPOINT}/api/v1/tenants/${tenantId}/jobpostings/${jobUuid}/applications/${candidate.uuid}/status/${newStatus}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        body: JSON.stringify(data),
      }
    );
    if (response.status === 200) {
      return { candidateId: candidate.uuid, newStatus };
    } else {
      throw new Error("Failed to update candidate status");
    }
  }
);

// Async Thunk for downloading documents
export const downloadDocument = createAsyncThunk(
  "candidates/downloadDocument",
  async ({ url, type }) => {
    var f = url.split("/");
    var fn = f[f.length - 1];
    const response = await fetch(
      `${API_ENDPOINT}/api/v1/storages/downloads/${fn}/type/${type}`,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        method: "GET",
      }
    );

    if (response.status === 200) {
      const blob = await response.blob();
      const fileURL = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = fileURL;
      a.download = `${url}.${blob.type.split("/")[1]}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      throw new Error(`Error downloading document: ${response.statusText}`);
    }
  }
);

/**
 * Applies compound filtering + sorting to allCandidates.
 * Used internally after any filter/sort/tab state change.
 */
const applyFiltersAndSort = (state) => {
  let result = [...state.allCandidates];

  // 1. Tab filter
  if (state.selectedTab !== "all") {
    result = result.filter(
      (item) => item.ApplicationStatus === state.selectedTab
    );
  }

  // 2. Text filter (FullName / Email / Sex / Skills)
  if (state.filterValue && state.filterValue.trim() !== "") {
    const filterLower = state.filterValue.toLowerCase();
    result = result.filter((item) => {
      if (state.filterKey === "ContactInformation.Email") {
        return (item.ContactInformation?.Email ?? "").toLowerCase().includes(filterLower);
      }
      return (item[state.filterKey] ?? "").toString().toLowerCase().includes(filterLower);
    });
  }

  // 3. Status filter
  if (state.statusFilter && state.statusFilter !== "") {
    result = result.filter(
      (item) => item.ApplicationStatus === state.statusFilter
    );
  }

  // 4. Score range filter
  if (state.scoreMin != null || state.scoreMax != null) {
    const min = state.scoreMin ?? 0;
    const max = state.scoreMax ?? 100;
    result = result.filter((item) => {
      const score = item.score_list?.total_score ?? 0;
      return score >= min && score <= max;
    });
  }

  // 5. Sorting
  const sortKey = state.sortBy;
  const direction = state.sortDirection === "asc" ? 1 : -1;

  result.sort((a, b) => {
    let aVal, bVal;

    if (sortKey === "FullName") {
      aVal = (a.FullName ?? "").toLowerCase();
      bVal = (b.FullName ?? "").toLowerCase();
      if (aVal < bVal) return -1 * direction;
      if (aVal > bVal) return 1 * direction;
      return 0;
    }

    if (sortKey === "Score") {
      aVal = a.score_list?.total_score ?? 0;
      bVal = b.score_list?.total_score ?? 0;
    } else if (sortKey === "ApplicationDate") {
      aVal = a.ApplicationDate ?? "";
      bVal = b.ApplicationDate ?? "";
    } else {
      aVal = a[sortKey] ?? 0;
      bVal = b[sortKey] ?? 0;
    }

    if (aVal < bVal) return 1 * direction;
    if (aVal > bVal) return -1 * direction;
    return 0;
  });

  state.selectedCandidates = result;
};

const initialState = candidatesAdapter.getInitialState({
  loading: false,
  error: null,
  selectedTab: "all",
  sortBy: "Score",
  sortDirection: "desc",
  filterKey: "FullName",
  filterValue: "",
  statusFilter: "",
  scoreMin: null,
  scoreMax: null,
  isCandidateDetailsModalOpen: false,
  selectedCandidates: [],
  selectedCandidate: {},
  allCandidates: [],
  candidates: [],
  currentJob: null,
  tenantId: null,
  jobUuid: null,
  downloading: false,
  downloadError: null,
});

const candidateSlice = createSlice({
  name: "candidates",
  initialState,
  reducers: {
    setSelectedTab: (state, action) => {
      state.selectedTab = action.payload.tab;
      applyFiltersAndSort(state);
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload.sortKey;
      if (action.payload.direction) {
        state.sortDirection = action.payload.direction;
      }
      applyFiltersAndSort(state);
    },
    toggleSortDirection: (state) => {
      state.sortDirection = state.sortDirection === "desc" ? "asc" : "desc";
      applyFiltersAndSort(state);
    },
    setFilterKey: (state, action) => {
      state.filterKey = action.payload;
    },
    setFilterValue: (state, action) => {
      state.filterValue = action.payload.filterValue;
      state.filterKey = action.payload.filterKey ?? state.filterKey;
      applyFiltersAndSort(state);
    },
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
      applyFiltersAndSort(state);
    },
    setScoreRange: (state, action) => {
      state.scoreMin = action.payload.min ?? null;
      state.scoreMax = action.payload.max ?? null;
      applyFiltersAndSort(state);
    },
    clearAllFilters: (state) => {
      state.filterValue = "";
      state.statusFilter = "";
      state.scoreMin = null;
      state.scoreMax = null;
      state.selectedTab = "all";
      state.sortBy = "Score";
      state.sortDirection = "desc";
      state.filterKey = "FullName";
      applyFiltersAndSort(state);
    },
    openCandidateDetailsModal: (state, action) => {
      state.isCandidateDetailsModalOpen = true;
      state.selectedCandidate = action.payload;
    },
    closeCandidateDetailsModal: (state) => {
      state.isCandidateDetailsModalOpen = false;
      state.selectedCandidate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCandidates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        state.loading = false;
        candidatesAdapter.setAll(state, action.payload.candidates);
        state.allCandidates = action.payload.allCandidates;
        state.selectedCandidates = action.payload.selectedCandidates;
        state.tenantId = action.payload.tenantId;
        state.jobUuid = action.payload.jobUuid;
      })
      .addCase(fetchCandidates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateCandidateStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCandidateStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { candidateId, newStatus } = action.payload;
        candidatesAdapter.updateOne(state, {
          id: candidateId,
          changes: { ApplicationStatus: newStatus },
        });
      })
      .addCase(updateCandidateStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(downloadDocument.pending, (state) => {
        state.downloading = true;
        state.downloadError = null;
      })
      .addCase(downloadDocument.fulfilled, (state) => {
        state.downloading = false;
      })
      .addCase(downloadDocument.rejected, (state, action) => {
        state.downloading = false;
        state.downloadError = action.error.message;
      });
  },
});

export const {
  setSelectedTab,
  setSortBy,
  toggleSortDirection,
  setFilterKey,
  setFilterValue,
  setStatusFilter,
  setScoreRange,
  clearAllFilters,
  openCandidateDetailsModal,
  closeCandidateDetailsModal,
} = candidateSlice.actions;

export const {
  selectAll: selectAllCandidates,
  selectById: selectCandidateById,
  selectIds: selectCandidateIds,
} = candidatesAdapter.getSelectors((state) => state.candidates);

export const selectCandidatesLoading = (state) => state.candidates.loading;
export const selectCandidatesError = (state) => state.candidates.error;
export const selectSelectedTab = (state) => state.candidates.selectedTab;
export const selectSelectedCandidates = (state) => state.candidates.selectedCandidates;
export const selectSortBy = (state) => state.candidates.sortBy;
export const selectSortDirection = (state) => state.candidates.sortDirection;
export const selectFilterKey = (state) => state.candidates.filterKey;
export const selectFilterValue = (state) => state.candidates.filterValue;
export const selectStatusFilter = (state) => state.candidates.statusFilter;
export const selectScoreRange = (state) => ({
  min: state.candidates.scoreMin,
  max: state.candidates.scoreMax,
});

/** Computes candidate counts per tab dynamically */
export const selectTabCounts = createSelector(
  [(state) => state.candidates.allCandidates],
  (allCandidates) => {
    const counts = { all: allCandidates.length };
    for (const candidate of allCandidates) {
      const status = candidate.ApplicationStatus ?? "Unknown";
      counts[status] = (counts[status] ?? 0) + 1;
    }
    return counts;
  }
);

/** Count of active filters for badge display */
export const selectActiveFilterCount = createSelector(
  [
    (state) => state.candidates.filterValue,
    (state) => state.candidates.statusFilter,
    (state) => state.candidates.scoreMin,
    (state) => state.candidates.scoreMax,
  ],
  (filterValue, statusFilter, scoreMin, scoreMax) => {
    let count = 0;
    if (filterValue && filterValue.trim() !== "") count++;
    if (statusFilter && statusFilter !== "") count++;
    if (scoreMin != null || scoreMax != null) count++;
    return count;
  }
);

export default candidateSlice.reducer;
